import React, { useEffect, useRef } from 'react';
import { contacts, content } from '../content';
import { Project } from '../types';
import { ArrowRightIcon, CheckIcon, CloseIcon, CodeIcon, LiveDotIcon } from './Icon';
// `.checklist`, `.chip` and `.btn` are shared with the sections.
import '../styles/Sections.css';
import '../styles/ProjectDialog.css';

const { ui } = content;

type ProjectDialogProps = {
  project: Project;
  onClose: () => void;
};

/**
 * The case study behind a card. Cards without `details` never open it, so the
 * dialog can assume the project has something to show.
 */
const FOCUSABLE = 'a[href], button, [tabindex]:not([tabindex="-1"])';

const ProjectDialog = ({ project, onClose }: ProjectDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const details = project.details!;

  // Focus moves into the dialog, stays there while it is open, returns to
  // whatever opened it, and Escape closes: what a keyboard visitor expects.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const stops = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      );
      if (stops.length === 0) return;

      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="dialog__backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={ui.dialogLabel(project.title)}
      >
        <button
          ref={closeRef}
          type="button"
          className="dialog__close"
          aria-label={ui.dialogClose}
          onClick={onClose}
        >
          <CloseIcon size={18} />
        </button>

        <header className="dialog__head">
          <span className="dialog__number">{project.number}</span>
          <h2 className="dialog__title">{project.title}</h2>
          <p className="dialog__subtitle">{project.subtitle}</p>
        </header>

        <p className="dialog__overview">{details.overview}</p>

        {/* Proof comes before the pitch. A visitor who can open the running
            service or read the source decides on their own, and everything
            below this point is only there to explain what they are looking
            at — so it must not sit at the bottom of the panel. */}
        {details.links && details.links.length > 0 && (
          <section className="dialog__block dialog__block--links">
            <h3 className="dialog__block-title">{ui.dialogLinks}</h3>
            <ul className="dialog__links">
              {details.links.map((link) => (
                <li key={link.href}>
                  <a
                    className={`dialog__link dialog__link--${link.kind}`}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link.kind === 'demo' ? (
                      <LiveDotIcon size={13} />
                    ) : (
                      <CodeIcon size={14} />
                    )}
                    {link.label}
                    <ArrowRightIcon size={15} />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {details.linksNote && <p className="dialog__links-note">{details.linksNote}</p>}

        {/* Screens come before the technical detail: a visitor decides whether
            the product looks real long before reading what is inside it. */}
        {details.shots && details.shots.length > 0 && (
          <section className="dialog__block">
            <h3 className="dialog__block-title">{ui.dialogShots}</h3>
            <div className="dialog__shots">
              {details.shots.map((shot) => (
                <figure key={shot.src} className="dialog__shot">
                  <img src={shot.src} alt={shot.alt} loading="lazy" decoding="async" />
                </figure>
              ))}
            </div>
          </section>
        )}

        <dl className="dialog__meta">
          {details.year && (
            <div className="dialog__meta-row">
              <dt>{ui.dialogYear}</dt>
              <dd>{details.year}</dd>
            </div>
          )}
          {details.role && (
            <div className="dialog__meta-row">
              <dt>{ui.dialogRole}</dt>
              <dd>{details.role}</dd>
            </div>
          )}
        </dl>

        <section className="dialog__block">
          <h3 className="dialog__block-title">{ui.dialogHighlights}</h3>
          <ul className="checklist">
            {details.highlights.map((item) => (
              <li key={item} className="checklist__item">
                <CheckIcon size={15} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="dialog__block">
          <h3 className="dialog__block-title">{ui.dialogStack}</h3>
          <ul className="dialog__stack">
            {project.stack.map((tech) => (
              <li key={tech} className="chip">
                {tech}
              </li>
            ))}
          </ul>
        </section>

        <footer className="dialog__foot">
          <a
            className="btn btn--primary"
            href={contacts.telegram}
            target="_blank"
            rel="noreferrer"
          >
            {ui.dialogWrite}
            <ArrowRightIcon size={15} />
          </a>
        </footer>
      </div>
    </div>
  );
};

export default ProjectDialog;
